import {FC, useEffect, useRef, useState} from "react";
import "./qr-styles.css";
import QrScanner from "qr-scanner";
import apiService from "../services/api-service";
import {ECheckAndApplyResultEnum, IQrScannerApiResponse} from "../types";
import {checkAndApplyResultEnumTransactionsData} from "../configs";
import {format} from "date-fns";

interface ScannerProps {
    additionalServiceId: string;
    onBackToSelectionScreen: () => void;
}

export const Scanner: FC<ScannerProps> = ({additionalServiceId, onBackToSelectionScreen}) => {
    const scanner = useRef<QrScanner>();
    const videoEl = useRef<HTMLVideoElement>(null);
    const qrBoxEl = useRef<HTMLDivElement>(null);
    const [qrOn, setQrOn] = useState<boolean>(true);
    const [, setAllowScanning] = useState<boolean>(true);
    const [scannerReady, setScannerReady] = useState(false);
    const scanning = useRef<boolean>(true);

    // Result
    const [scannedResult, setScannedResult] = useState<string | undefined>("");
    const [apiResponse, setApiResponse] = useState<IQrScannerApiResponse>();

    // Success
    const onScanSuccess = async (result: QrScanner.ScanResult) => {
        if (!scanning.current) return;
        // 🖨 Print the "result" to the browser console.
        scanning.current = false;
        setAllowScanning(false);
        console.log("scanned: ", result.data);
        if (result.data === "") return;
        const res = await apiService.check(additionalServiceId, result.data);
        if (res.status === 200) {
            setApiResponse(res.data as IQrScannerApiResponse);
        }

        setScannedResult(result?.data);
    };

    // Fail
    const onScanFail = (err: string | Error) => {
        // 🖨 Print the "err" to the browser console.
        if (scanning.current) console.log(err);
    };

    useEffect(() => {
        if (videoEl?.current && !scanner.current) {
            // 👉 Instantiate the QR Scanner
            scanner.current = new QrScanner(videoEl?.current, onScanSuccess, {
                onDecodeError: onScanFail,
                // 📷 This is the camera-facing mode. In mobile devices, "environment" means back camera and "user" means front camera.
                preferredCamera: "environment",
                // 🖼 This will help us position our "QrFrame.svg" so that the user can only scan when qr code is put in between our QrFrame.svg.
                highlightScanRegion: true,
                // 🔥 This will produce a yellow (default color) outline around the qr code that we scan, showing a proof that our qr-scanner is scanning that qr code.
                highlightCodeOutline: true,
                // 📦 A custom div which will pair with the "highlightScanRegion" option above 👆. This gives us full control over our scan region.
                overlay: qrBoxEl?.current || undefined,
            });

            // 🚀 Start QR Scanner
            scanner?.current
                ?.start()
                .then(() => {
                    setQrOn(true);
                    setScannerReady(true)
                })
                .catch((err) => {
                    if (err) {
                        setQrOn(false);
                        setScannerReady(true); // Still mark ready so the user can at least go back
                    }
                });
        }

        // 🧹 Clean up on unmounting.
        // 🚨 This removes the QR Scanner from rendering and using the camera when it is closed or removed from the UI.
        return () => {
            if (!videoEl?.current) {
                scanner?.current?.stop();
            }
        };
    }, []);

    // ❌ If "camera" is not allowed in browser permissions, show an alert.
    useEffect(() => {
        if (!qrOn)
            alert(
                "Camera is blocked or not accessible. Please allow camera in your browser permissions and Reload."
            );
    }, [qrOn]);

    console.info("apiResponse", apiResponse);

    return (
        <div className="qr-reader relative">
            {scannerReady && (

                <button
                    onClick={onBackToSelectionScreen}
                    className="absolute top-4 left-4 z-[10000] px-6 py-2 font-medium bg-black text-white rounded shadow hover:bg-blue-600"
                >
                    Zpět
                </button>
            )}
            {/* QR */}
            <video ref={videoEl}></video>
            <div ref={qrBoxEl} className="qr-box">
                <img
                    src={"/qr-frame.svg"}
                    alt="Qr Frame"
                    width={256}
                    height={256}
                    className="qr-frame"
                />
            </div>

            {/* Show Data Result if scan is success */}
            {scannedResult && !scanning.current && (
                <div
                    style={{
                        position: "fixed", // Ensures it covers the whole screen
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        zIndex: 99999,
                        backgroundColor:
                            apiResponse!.result === ECheckAndApplyResultEnum.OK
                                ? "green"
                                : "red", // Green if apiResponse is true, red otherwise
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        color: "white",
                        fontSize: "24px",
                    }}
                    onClick={() => {
                        scanning.current = true;
                        setAllowScanning(true);
                    }}
                >
                    <div className="p-4 rounded-lg">
                        Jméno: {apiResponse?.participantName}
                    </div>
                    <div className="p-4 rounded-lg">
                        Výsledek:{" "}
                        {checkAndApplyResultEnumTransactionsData[apiResponse!.result]}
                    </div>
                    {apiResponse?.appliedDate && (
                        <div className="p-4 rounded-lg">
                            Využito:{" "}
                            {format(new Date(apiResponse.appliedDate), "dd.MM.yyyy HH:mm")}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
